import { SessionHealthEvent } from '@augloop/tracelogger/dist/src/session-health-event';
import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { now } from './now';

/** Who reported session health event */
export enum SessionHealthSource {
    /** Shouldn't be used */
    Unknown = 0,

    /** Any AL owned code (serviceName can be used to further pinpoint the service) */
    Core = 1,

    /** Workflow owned code */
    Workflow = 2,

    /** Host owned code in AL service */
    SessionExtension = 3,

    /** Host reported error (i.e. via web socket) */
    Client = 4,

    /** Our runtime on the host reported error (i.e. via web socket) */
    ClientRuntime = 5
}

/** What is the underlying reason for session health event. This is to the best of knowledge of the reporting code. Combining multiple session health events could reveal a different reason (for example network). */
export enum SessionHealthReason {
    /** Used when we can not determine reason when reporting the event. Other events could provide clues what was the reason. */
    Unknown = 0,

    /** Failure in AL owned code */
    Core = 1,

    /** Failure in workflow owned code */
    Workflow = 2,

    /** Failure in session extension code (host owned code in AL service) */
    SessionExtension = 3,

    /** Failure in host owned code (client and/or server) */
    Client = 4,

    /** Failure happening due to network flakiness */
    Network = 5,

    /** For example AAD token exchange service or Azure Blob Storage */
    AugLoopDependency = 6,

    /** For example Editor service or Cognitive services */
    WorkflowDependency = 7,

    /** Failure in our runtime code on the host */
    ClientRuntime = 8
}

export enum SessionHealthImpact {
    /** Used when we can not determine impact */
    Unknown = 0,

    /** Used when event has no impact on session health */
    None = 1,

    /** Used when event failure or slowness would result in missing input for AL processing (workflows) */
    MissingInput = 2,

    /** Used when event failure or slowness would result in missing output (annotations) for the client */
    MissingOutput = 3
}

export type AffectedWorkflow = 'All' | string /* workflow id */;

export interface ISessionHealthArgs {
    source: SessionHealthSource;
    reason: SessionHealthReason;
    /** Used to describe which dependency failed if reason=Dependency */
    reasonDependency?: string;
    /** Optionally set. Analyzer code needs to take this into account so it is visible in reported metrics/dashboards/monitors. Default analyzer takes the subreason into account. */
    subReason?: string;
    sessionHealthEventName: string;
    impact: SessionHealthImpact;
    success: boolean;
    durationMs?: number;
    count?: number;
    message: string;
    /**
     * Below field describes which workflows are affected. Special value "All". If WorkflowXyz failed executing value will be ["WorkflowXyz"], if there is a missing input from the client value will be ["All"].
     * We might want to add more fields as service evolves, for example if scenario health/AL service output is no longer solely defined by workflowId.
     */
    affectedWorkflows?: AffectedWorkflow[];
    resourceId?: string;
    dimension0?: string;
    dimension1?: string;
    dimension2?: string;
    dimension3?: string;
    cv?: string;
    resultSignature?: string;
    resultDescription?: string;
}

/**
 * Example session health events
 * Full file seeding:
 *  +-------------------------------------------------------------------------------------------+---------+---------+-----------------+--------------+---------+------------+-----------------------------------+
 *  |                                       Example name                                        | source  | reason  |    eventName    |    impact    | success | durationMs |              message              |
 *  +-------------------------------------------------------------------------------------------+---------+---------+-----------------+--------------+---------+------------+-----------------------------------+
 *  | Received FFS as expected (will not change session to "yellow"/"warning" or "red"/"error") | Core    | Client  | FullFileSeeding | MissingInput | true    |       4983 | Received full file seeding        |
 *  | Received FFS slowly (will result in "yellow"/"warning" for the session)                   | Core    | Client  | FullFileSeeding | MissingInput | true    |      27931 | Received full file seeding        |
 *  | Did not receive FFS (will result in "red"/"error" for the session)                        | Core    | Client  | FullFileSeeding | MissingInput | false   |      30000 | Did not receive full file seeding |
 *  | Failed to apply sync message from FFS due to bug in AL code                               | Core    | Core    | FullFileSeeding | MissingInput | false   |       4983 | Failed to apply FFS               |
 *  +-------------------------------------------------------------------------------------------+---------+---------+-----------------+--------------+---------+------------+-----------------------------------+
 * Applying sync message:
 *  +--------------------------------------------------------------------------------------------------------------+---------+---------+------------------+--------------+---------+------------+----------------------------------+
 *  |                                                 Example name                                                 | source  | reason  |    eventName     |    impact    | success | durationMs |             message              |
 *  +--------------------------------------------------------------------------------------------------------------+---------+---------+------------------+--------------+---------+------------+----------------------------------+
 *  | Applied sync message with seq>0 as expected (will not change session to "yellow"/"warning" or "red"/"error") | Core    | Core    | ApplySyncMessage | MissingInput | true    |         84 | Successfuly applied sync message |
 *  | Slow to apply sync message with seq>0 (will result in "yellow"/"warning" for the session)                    | Core    | Core    | ApplySyncMessage | MissingInput | true    |       6843 | Successfuly applied sync message |
 *  | Failed to apply sync message with seq>0 due to bug in AL code                                                | Core    | Core    | ApplySyncMessage | MissingInput | false   |       2321 | Failed to apply SyncMessage      |
 *  +--------------------------------------------------------------------------------------------------------------+---------+---------+------------------+--------------+---------+------------+----------------------------------+
 * Executing workflow:
 *  +-------------------------------------------------------------------------------+----------+--------------------+------------------+-------------------+---------------+---------+------------+-------------------+--------------------------------------+
 *  |                                 Example name                                  |  source  |       reason       | reasonDependency |     eventName     |    impact     | success | durationMs | affectedWorkflows |               message                |
 *  +-------------------------------------------------------------------------------+----------+--------------------+------------------+-------------------+---------------+---------+------------+-------------------+--------------------------------------+
 *  | Workflow successfully executed                                                | Core     | Workflow           |                  | WorkflowExecution | MissingOutput | true    |        843 | ["proofingV2"]    | Successfuly executed workflow        |
 *  | Workflow execution failed due to pubsub problem                               | Core     | Core               |                  | WorkflowExecution | MissingOutput | false   |       4749 | ["proofingV2"]    | Pubsub failed                        |
 *  | Workflow execution failed due to workflow failure                             | Workflow | Workflow           |                  | WorkflowExecution | MissingOutput | false   |       4749 | ["proofingV2"]    | Unsupported length of input text     |
 *  | Workflow execution failed due to workflow dependency failure (Editor service) | Workflow | WorkflowDependency | EditorService    | WorkflowExecution | MissingOutput | false   |       4749 | ["proofingV2"]    | Editor service internal server error |
 *  +-------------------------------------------------------------------------------+----------+--------------------+------------------+-------------------+---------------+---------+------------+-------------------+--------------------------------------+
 * Activating workflow:
 *  +-----------------------------------------------------------------------------------------------------------------------------------------------------------------------+---------+-------------------+----------------------+------------------+---------------+---------+------------+------------------------------------+
 *  |                                                                             Example name                                                                              | source  |      reason       |   reasonDependency   |    eventName     |    impact     | success | durationMs |              message               |
 *  +-----------------------------------------------------------------------------------------------------------------------------------------------------------------------+---------+-------------------+----------------------+------------------+---------------+---------+------------+------------------------------------+
 *  | Received token which failed validation                                                                                                                                | Core    | Client            |                      | TokenProvision   | Unknown       | false   |        321 | Token failed validation            |
 *  | Token exchange failed                                                                                                                                                 | Core    | AugLoopDependency | TokenExchangeService | TokenExchange    | Unknown       | false   |       2035 | Exchange request failed            |
 *  | Workflow not executing because token is not available (reason=Unkown because we do not know why token is missing, but one of above events could shed a light on that) | Core    | Unknown           |                      | ActivateWorkflow | MissingOutput | false   |          0 | Missing token                      |
 *  | Workflow not executing because annotation was not activated                                                                                                           | Core    | Client            |                      | ActivateWorkflow | MissingOutput | true    |          0 | Workflow annotations not requested |
 *  | Workflow successfully activated                                                                                                                                       | Core    | Client            |                      | ActivateWorkflow | Unknown       | true    |          0 | Workflow activated                 |
 *  +-----------------------------------------------------------------------------------------------------------------------------------------------------------------------+---------+-------------------+----------------------+------------------+---------------+---------+------------+------------------------------------+
 */
export class SessionHealth extends SessionHealthEvent {
    public eventName: string = 'SessionHealth';
    public timestamp: number;

    private startTime: number;

    public constructor(properties: ISessionHealthArgs, clientMetadata?: IClientMetadata) {
        super({
            source: SessionHealthSource[properties.source],
            reason: SessionHealthReason[properties.reason],
            reasonDependency: properties.reasonDependency,
            subReason: properties.subReason,
            sessionHealthEventName: properties.sessionHealthEventName,
            impact: SessionHealthImpact[properties.impact],
            success: properties.success,
            durationMs: properties.durationMs ?? 0,
            count: typeof properties.count === 'number' ? properties.count : 1,
            message: properties.message,
            affectedWorkflows: (properties.affectedWorkflows ?? []).join(','),
            resourceId: properties.resourceId,
            dimension0: properties.dimension0,
            dimension1: properties.dimension1,
            dimension2: properties.dimension2,
            dimension3: properties.dimension3,
            cv: properties.cv,
            resultSignature: properties.resultSignature,
            resultDescription: properties.resultDescription
        });

        if (clientMetadata) {
            this.setClientMetadata(clientMetadata);
        }
    }

    public setClientMetadata(clientMetadata: IClientMetadata): SessionHealth {
        if (clientMetadata) {
            this.clientAppName = clientMetadata.appName;
            this.clientAppPlatform = clientMetadata.appPlatform;
            this.clientAppVersion = clientMetadata.appVersion;
            this.clientFlights = clientMetadata.flights;
            this.clientReleaseAudienceGroup = clientMetadata.releaseAudienceGroup;
            this.clientReleaseChannel = clientMetadata.releaseChannel;
            this.clientReleaseFork = clientMetadata.releaseFork;
            this.clientRuntimeVersion = clientMetadata.runtimeVersion;
            this.clientSessionId = clientMetadata.sessionId;
        }
        return this;
    }

    public setReason(reason: SessionHealthReason): SessionHealth {
        this.reason = SessionHealthReason[reason];
        return this;
    }

    public setSource(source: SessionHealthSource): SessionHealth {
        this.source = SessionHealthSource[source];
        return this;
    }

    public setImpact(impact: SessionHealthImpact): SessionHealth {
        this.impact = SessionHealthImpact[impact];
        return this;
    }

    public setAffectedWorkflows(affectedWorkflows: AffectedWorkflow[]): SessionHealth {
        this.affectedWorkflows = (affectedWorkflows ?? []).join(',');
        return this;
    }

    public getAffectedWorkflows(): AffectedWorkflow[] {
        return this.affectedWorkflows.split(',');
    }

    public start(): SessionHealth {
        this.startTime = now();
        return this;
    }

    public stop(): SessionHealth {
        const stopTime = now();
        this.durationMs = Math.round(stopTime - this.startTime);
        return this;
    }
}
